import React from 'react';
import { X } from 'lucide-react';
import { Overlay, AlbumOverlayConfig, OverlayAnchor, OverlaySize, OVERLAY_SIZE_LABELS } from '../../types';
import { overlaysApi } from '../../api/overlays';
import styles from './OverlayConfigCard.module.css';

const CHECKERBOARD =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20'%3E" +
  "%3Crect width='10' height='10' fill='%23e2e8f0'/%3E" +
  "%3Crect x='10' width='10' height='10' fill='%23f8fafc'/%3E" +
  "%3Crect y='10' width='10' height='10' fill='%23f8fafc'/%3E" +
  "%3Crect x='10' y='10' width='10' height='10' fill='%23e2e8f0'/%3E" +
  "%3C/svg%3E\")";

const ANCHOR_GRID: { anchor: OverlayAnchor }[] = [
  { anchor: 'top-left' },
  { anchor: 'top-center' },
  { anchor: 'top-right' },
  { anchor: 'middle-left' },
  { anchor: 'middle-center' },
  { anchor: 'middle-right' },
  { anchor: 'bottom-left' },
  { anchor: 'bottom-center' },
  { anchor: 'bottom-right' },
];

const SIZES: OverlaySize[] = ['xs', 'sm', 'md', 'lg', 'xl'];

interface OverlayConfigCardProps {
  overlay: Overlay;
  config: AlbumOverlayConfig;
  /**
   * Anchors that are unavailable because another overlay (manual or system
   * default) already occupies that position. The card's own current anchor
   * MUST be excluded from this set by the parent.
   */
  reservedAnchors?: Set<OverlayAnchor>;
  onChange: (updated: AlbumOverlayConfig) => void;
  onRemove: () => void;
}

export function OverlayConfigCard({ overlay, config, reservedAnchors, onChange, onRemove }: OverlayConfigCardProps) {
  const update = (partial: Partial<AlbumOverlayConfig>) =>
    onChange({ ...config, ...partial });

  const reserved = reservedAnchors ?? new Set<OverlayAnchor>();

  return (
    <div className={styles['card']}>
      {/* Header: thumbnail + name + remove */}
      <div className={styles['header']}>
        <div
          className={`${styles['thumbnail']} ${styles['thumbnail-bg']}`}
          style={{ '--bg-image': CHECKERBOARD } as React.CSSProperties}
        >
          <img
            src={overlaysApi.fileUrl(overlay._id)}
            alt={overlay.name}
            className={styles['thumbnail-img']}
          />
        </div>
        <span className={styles['overlay-name']}>
          {overlay.name}
        </span>
        <button
          onClick={onRemove}
          className={styles['remove-button']}
          aria-label={`Remove ${overlay.name}`}
          title="Remove overlay"
        >
          <X className={styles['icon-lg']} />
        </button>
      </div>

      {/* Anchor position — 3x3 grid (admin Branding tab style) */}
      <div>
        <p className={styles['section-label']}>Position</p>
        <div className={styles['anchor-grid']}>
          {ANCHOR_GRID.map(({ anchor }) => {
            const active = config.anchor === anchor;
            const disabled = !active && reserved.has(anchor);
            return (
              <button
                key={anchor}
                type="button"
                onClick={() => !disabled && update({ anchor })}
                disabled={disabled}
                title={disabled ? 'Position is already occupied' : anchor}
                aria-label={`Anchor ${anchor}`}
                aria-pressed={active}
                aria-disabled={disabled}
                className={[
                  styles['anchor-cell'],
                  active ? styles['anchor-cell-active'] : '',
                  disabled ? styles['anchor-cell-disabled'] : '',
                ].filter(Boolean).join(' ')}
              >
                {active && <span className={styles['anchor-dot']} />}
                {disabled && <span className={styles['anchor-x']}>×</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* Size */}
      <div>
        <p className={styles['section-label']}>Size</p>
        <div className={styles['size-row']}>
          {SIZES.map((s) => (
            <button
              key={s}
              onClick={() => update({ size: s })}
              className={[
                styles['size-button'],
                config.size === s ? styles['active'] : '',
              ].filter(Boolean).join(' ')}
              title={OVERLAY_SIZE_LABELS[s]}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      {/* Transparency — admin Branding tab style slider */}
      <div>
        <p className={styles['section-label']}>Transparency</p>
        <div className={styles['opacity-row']}>
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={Math.round(config.opacity * 100)}
            onChange={(e) => update({ opacity: Number(e.target.value) / 100 })}
            className={styles['opacity-slider']}
            aria-label="Opacity"
          />
          <span className={styles['opacity-value']}>
            {Math.round(config.opacity * 100)}%
          </span>
        </div>
      </div>
    </div>
  );
}
